// UI strings for the lessons area (list, lesson page, MDX components, the end-of-lesson check),
// Finnish (default) and English side by side. Lesson bodies themselves are Finnish only.
import { defineArea } from '../area';

export const lessonsFi = {
  title: 'Oppitunnit',
  number: (n: number) => `Oppitunti ${n}`,
  upcoming: 'tulossa',
  state: {
    locked: 'lukittu',
    unwritten: 'kirjoittamatta',
    open: 'avoinna',
    passed: 'läpäisty',
  } as Record<'locked' | 'unwritten' | 'open' | 'passed', string>,
  unlocks: (date: string) => `avautuu ${date}`,
  unlocked: (date: string) => `avautui ${date}`,
  best: (pct: number) => `paras tulos ${pct}\u00a0%`,
  attempts: (n: number) => `${n} ${n === 1 ? 'yritys' : 'yritystä'}`,
  empty: 'Oppitunteja ei vielä ole.',
  // lesson page
  back: 'Kaikki oppitunnit',
  notFound: 'Oppituntia ei löytynyt.',
  // shortDate already ends in "." ("su 6.12."): no second period
  lockedPage: (date: string) => `Tämä oppitunti avautuu teille molemmille ${date}, kun olette läpäisseet edellisen. Sisältöä ei näytetä etukäteen.`,
  unwrittenPage: 'Tätä oppituntia ei ole vielä kirjoitettu.',
  loading: 'Ladataan oppituntia…',
  loadFailed: 'Oppitunnin lataaminen ei onnistunut. Yritä uudelleen, kun yhteys toimii.',
  status: 'Tilanne',
  replay: 'Voit lukea oppitunnin ja tehdä loppukokeen uudelleen milloin tahansa. Läpäisy säilyy.',
  notPassedYet: 'Loppukoe on oppitunnin lopussa. Kun läpäiset sen (80\u00a0%), oppitunnin sanat tulevat päivän kortteihin.',
  passedOn: (date: string) => `Läpäisty ${date}.`,
  sources: 'Lähteet',
  flagLesson: 'Huomautus tästä oppitunnista',
  // components
  missing: (id: string) => `puuttuu: ${id}`,
  compare: 'Verrattuna suomeen',
  compareFi: 'suomi',
  compareHv: 'valyria',
  practice: 'harjoituslause',
  practiceNote: 'ei virallinen lause',
  note: 'Huomio',
  vocab: 'Oppitunnin sanat',
  vocabEmpty: 'Tällä oppitunnilla ei ole omia sanoja.',
  noMeaning: 'merkitys puuttuu',
  paradigm: (hv: string) => `Sanan ${hv} muodot`,
  noForms: 'Muotoja ei löytynyt.',
  interlinear: 'Sana sanalta',
  wikiExample: 'wikin esimerkki',
  // the translation after a High Valyrian example (HvGloss.tsx): where it comes from (tooltip)
  trFrom: {
    dict: 'merkitys sanakirjasta',
    official: 'virallinen käännös',
    lesson: 'oppitunnin oma käännös',
  } as Record<'dict' | 'official' | 'lesson', string>,
  /** Conjunctive lengthening (ābrā = 'and the woman'): the meaning starts with this. */
  trAnd: 'ja',
  // check
  check: {
    title: 'Loppukoe',
    intro: (n: number) =>
      `${n} ${n === 1 ? 'tehtävä' : 'tehtävää'}. Läpäisyyn tarvitaan 80\u00a0%. Kun läpäiset kokeen, tämän oppitunnin sanat tulevat päivän kortteihin.`,
    replayIntro: 'Olet jo läpäissyt tämän oppitunnin. Voit tehdä kokeen uudelleen harjoitukseksi; läpäisy säilyy.',
    start: 'Aloita loppukoe',
    again: 'Tee koe uudelleen',
    passed: (pct: number) => `Läpäisty, ${pct}\u00a0%. Oppitunnin sanat tulevat nyt päivän kortteihin.`,
    passedAgain: (pct: number) => `${pct}\u00a0%. Läpäisy on jo kirjattu.`,
    failed: (pct: number) => `${pct}\u00a0%. Läpäisyyn tarvitaan 80\u00a0%. Kertaa oppitunti ja yritä uudelleen.`,
    queued: 'Tulos on tallessa tällä laitteella ja lähtee, kun yhteys palaa.',
    noItems: 'Tälle oppitunnille ei saatu koottua tehtäviä. Merkitse oppitunti tarkistettavaksi.',
    waiting: 'Odotetaan taivutusmuotojen hakemistoa, jotta vastaukset voidaan tarkistaa oikein.',
  },
};

export const lessonsEn: typeof lessonsFi = {
  title: 'Lessons',
  number: (n) => `Lesson ${n}`,
  upcoming: 'coming',
  state: {
    locked: 'locked',
    unwritten: 'not written yet',
    open: 'open',
    passed: 'passed',
  },
  unlocks: (date) => `unlocks ${date}`,
  unlocked: (date) => `unlocked ${date}`,
  best: (pct) => `best ${pct}%`,
  attempts: (n) => `${n} ${n === 1 ? 'attempt' : 'attempts'}`,
  empty: 'No lessons yet.',
  back: 'All lessons',
  notFound: 'Lesson not found.',
  lockedPage: (date) => `This lesson opens for both of you on ${date} once you have passed the one before. Its content is not shown in advance.`,
  unwrittenPage: 'This lesson has not been written yet.',
  loading: 'Loading the lesson…',
  loadFailed: 'The lesson could not be loaded. Try again when you are online.',
  status: 'Status',
  replay: 'You can reread the lesson and retake its check any time. A pass is kept.',
  notPassedYet: 'The check is at the end of the lesson. Passing it (80%) releases the lesson’s words into the daily cards.',
  passedOn: (date) => `Passed ${date}.`,
  sources: 'Sources',
  flagLesson: 'A note about this lesson',
  missing: (id) => `missing: ${id}`,
  compare: 'Compared with Finnish',
  compareFi: 'Finnish',
  compareHv: 'Valyrian',
  practice: 'practice sentence',
  practiceNote: 'not an official sentence',
  note: 'Note',
  vocab: 'Words in this lesson',
  vocabEmpty: 'This lesson has no words of its own.',
  noMeaning: 'no meaning yet',
  paradigm: (hv) => `Forms of ${hv}`,
  noForms: 'No forms found.',
  interlinear: 'Word by word',
  wikiExample: 'wiki example',
  trFrom: {
    dict: 'meaning from the dictionary',
    official: 'official translation',
    lesson: 'the lesson’s own translation',
  },
  trAnd: 'and',
  check: {
    title: 'Lesson check',
    intro: (n) => `${n} ${n === 1 ? 'task' : 'tasks'}. You need 80% to pass. Passing releases this lesson’s words into the daily cards.`,
    replayIntro: 'You have already passed this lesson. You can take the check again as practice; the pass is kept.',
    start: 'Start the check',
    again: 'Take the check again',
    passed: (pct) => `Passed, ${pct}%. The lesson’s words now come into the daily cards.`,
    passedAgain: (pct) => `${pct}%. The pass is already recorded.`,
    failed: (pct) => `${pct}%. You need 80% to pass. Review the lesson and try again.`,
    queued: 'The result is saved on this device and goes to the server when you are back online.',
    noItems: 'No tasks could be built for this lesson. Please flag the lesson.',
    waiting: 'Waiting for the index of inflected forms, so answers can be checked properly.',
  },
};

// Messages.lessons (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('lessons', lessonsFi, lessonsEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof lessonsFi> {}
}
